// The `storage` interface: a filesystem-shaped store. Paths, bytes and a
// scope; nothing from chess. A provider (storage-dexie, storage-opfs, a
// native adapter) implements `Storage`; consumers receive a scoped handle
// and cannot reach outside it.

export const STORAGE_API = 'storage';
export const STORAGE_VERSION = '1.0.0';

/** What a file holds. Text for records, bytes for anything else. */
export type FileData = string | Uint8Array;

export interface Entry {
  name: string;
  kind: 'file' | 'dir';
}

export interface Storage {
  /** The file's data, or undefined if there is no such file. */
  read(path: string): Promise<FileData | undefined>;
  /** The file's data as text, or undefined if there is no such file. */
  readText(path: string): Promise<string | undefined>;
  /** Writes the whole file. Atomic per file: a reader sees the old or the new. */
  write(path: string, data: FileData): Promise<void>;
  /** Whether a file exists at this path. Directories are implied by their files. */
  exists(path: string): Promise<boolean>;
  /** The entries directly under a directory, files and subdirectories, sorted by name. */
  list(dir: string): Promise<Entry[]>;
  /** Removes a file, or a directory and everything under it. Removing nothing is not an error. */
  remove(path: string): Promise<void>;
  /** A handle whose every path is relative to `dir`, and which cannot see outside it. */
  scope(dir: string): Storage;
}

/**
 * Normalises a path: forward slashes, no leading or trailing slash, no empty
 * segments. Rejects `.` and `..`, which would let a scope escape.
 */
export function normalizePath(path: string): string {
  const parts = path.split('/').filter((p) => p.length > 0);
  for (const p of parts) {
    if (p === '.' || p === '..') throw new Error(`invalid path segment "${p}" in "${path}"`);
  }
  return parts.join('/');
}

/** Joins a scope's base with a relative path. An empty base is the root. */
export function joinPath(base: string, path: string): string {
  const b = normalizePath(base);
  const p = normalizePath(path);
  return b && p ? `${b}/${p}` : b || p;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function toText(data: FileData): string {
  return typeof data === 'string' ? data : decoder.decode(data);
}

export function toBytes(data: FileData): Uint8Array {
  return typeof data === 'string' ? encoder.encode(data) : data;
}

/**
 * Wraps any root `Storage` so that every path is prefixed with `base`.
 * Providers implement the root; this gives them `scope` for free.
 */
export function scoped(root: Storage, base: string): Storage {
  const b = normalizePath(base);
  const at = (path: string) => joinPath(b, path);
  // Async so that a bad path rejects rather than throws, like every provider.
  return {
    read: async (path) => root.read(at(path)),
    readText: async (path) => root.readText(at(path)),
    write: async (path, data) => root.write(at(path), data),
    exists: async (path) => root.exists(at(path)),
    list: async (dir) => root.list(at(dir)),
    remove: async (path) => root.remove(at(path)),
    scope: (dir) => scoped(root, at(dir)),
  };
}

/**
 * Derives the entries directly under `dir` from a list of every file path
 * beneath it. Shared by providers that keep files in a flat table.
 */
export function entriesUnder(dir: string, paths: Iterable<string>): Entry[] {
  const d = normalizePath(dir);
  const prefix = d ? `${d}/` : '';
  const seen = new Map<string, Entry['kind']>();
  for (const p of paths) {
    if (!p.startsWith(prefix)) continue;
    const rest = p.slice(prefix.length);
    if (!rest) continue;
    const slash = rest.indexOf('/');
    if (slash < 0) seen.set(rest, 'file');
    else if (!seen.has(rest.slice(0, slash))) seen.set(rest.slice(0, slash), 'dir');
  }
  return [...seen.entries()]
    .map(([name, kind]) => ({ name, kind }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
